'use client';
import React, { useState, useRef, useEffect } from 'react';
import Link from 'next/link';
import { Heart, BedDouble, Bath, Maximize2, MapPin, ChevronLeft, ChevronRight, ArrowRight, CheckCircle } from 'lucide-react';
import AppImage from '@/components/ui/AppImage';
import { getFeaturedListings, mapToFeaturedListing, type ListingRow } from '@/lib/services/listingsService';

interface FeaturedListing {
  id: string;
  title: string;
  price: string;
  pricePerSqft?: string;
  location: string;
  beds: number;
  baths: number;
  area: string;
  type: string;
  status: 'For Sale' | 'For Rent';
  isFeatured: boolean;
  isVerified: boolean;
  image: string;
  imageAlt: string;
  seller: string;
  sellerType: string;
}

function PropertyCard({ listing }: { listing: FeaturedListing }) {
  const [isFav, setIsFav] = useState(false);

  return (
    <div className="property-card group flex-shrink-0" style={{ width: '308px' }}>
      {/* Image */}
      <div className="relative img-zoom-wrap" style={{ height: '210px' }}>
        <AppImage
          src={listing.image}
          alt={listing.imageAlt}
          fill
          className="object-cover" />
        
        {/* Gradient */}
        <div className="absolute inset-0" style={{ background: 'linear-gradient(180deg, transparent 50%, rgba(0,0,0,0.4) 100%)' }} />

        {/* Badges */}
        <div className="absolute top-3 left-3 flex flex-col gap-1.5">
          {listing.isFeatured && <span className="badge-featured">Featured</span>}
        </div>

        {/* Fav button */}
        <div className="absolute top-3 right-3">
          <button
            onClick={() => setIsFav(!isFav)}
            className={`w-8 h-8 rounded-full flex items-center justify-center transition-all shadow-sm ${
              isFav ? 'bg-[#D93025] text-white' : 'bg-white/95 text-[#6E6E73] hover:text-[#D93025]'
            }`}
            aria-label="Save to favorites">
            <Heart size={13} fill={isFav ? 'currentColor' : 'none'} />
          </button>
        </div>

        {/* Status badge */}
        <div className="absolute bottom-3 left-3">
          <span className={listing.status === 'For Sale' ? 'badge-for-sale' : 'badge-for-rent'}>
            {listing.status}
          </span>
        </div>
      </div>

      {/* Content */}
      <div className="p-4">
        {/* Type chip */}
        <div className="flex items-center justify-between mb-2">
          <span className="text-[11px] font-600 text-[#6E6E73] bg-[#F5F5F7] px-2.5 py-1 rounded-full">{listing.type}</span>
          {listing.isVerified &&
            <span className="flex items-center gap-1 text-[11px] font-600 text-[#16A34A]">
              <CheckCircle size={11} fill="currentColor" />
              Verified
            </span>
          }
        </div>

        <h3 className="font-700 text-[#1D1D1F] mb-1.5 leading-snug" style={{ fontSize: '14px', letterSpacing: '-0.01em' }}>
          <Link href={`/listing-detail-page?id=${listing.id}`} className="hover:text-[#D93025] transition-colors">
            {listing.title}
          </Link>
        </h3>

        {/* Location */}
        <div className="flex items-center gap-1 mb-3">
          <MapPin size={11} className="text-[#D93025] flex-shrink-0" />
          <span className="text-[12px] text-[#6E6E73] truncate">{listing.location}</span>
        </div>

        {/* Specs */}
        <div className="flex items-center gap-3 mb-3 pb-3 border-b border-[#F0F0F5]">
          {listing.beds > 0 &&
            <span className="flex items-center gap-1 text-[12px] text-[#6E6E73]">
              <BedDouble size={12} className="text-[#D93025]" />
              {listing.beds}
            </span>
          }
          {listing.baths > 0 &&
            <span className="flex items-center gap-1 text-[12px] text-[#6E6E73]">
              <Bath size={12} className="text-[#D93025]" />
              {listing.baths}
            </span>
          }
          {listing.area &&
            <span className="flex items-center gap-1 text-[12px] text-[#6E6E73]">
              <Maximize2 size={12} className="text-[#D93025]" />
              {listing.area}
            </span>
          }
        </div>

        {/* Price + CTA */}
        <div className="flex items-end justify-between">
          <div>
            <p className="font-700 text-[#D93025] price-tag" style={{ fontSize: '18px' }}>{listing.price}</p>
            {listing.pricePerSqft &&
              <p className="text-[11px] text-[#A1A1A6]">{listing.pricePerSqft}</p>
            }
          </div>
          <Link
            href={`/listing-detail-page?id=${listing.id}`}
            className="flex items-center gap-1 text-[12px] font-600 text-[#D93025] hover:text-[#B71C1C] transition-colors group/link">
            Details
            <ArrowRight size={11} className="group-hover/link:translate-x-0.5 transition-transform" />
          </Link>
        </div>
      </div>
    </div>
  );
}

// Skeleton card for loading state
function SkeletonCard() {
  return (
    <div className="property-card flex-shrink-0 animate-pulse" style={{ width: '308px' }}>
      <div className="bg-[#E2E2E7] rounded-t-[16px]" style={{ height: '210px' }} />
      <div className="p-4 space-y-3">
        <div className="h-4 bg-[#E2E2E7] rounded-full w-1/3" />
        <div className="h-4 bg-[#E2E2E7] rounded-full w-3/4" />
        <div className="h-3 bg-[#E2E2E7] rounded-full w-1/2" />
        <div className="h-6 bg-[#E2E2E7] rounded-full w-1/3" />
      </div>
    </div>
  );
}

export default function FeaturedListings() {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);
  const [listings, setListings] = useState<FeaturedListing[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getFeaturedListings(8).then((rows) => {
      setListings(rows.map(mapToFeaturedListing));
      setLoading(false);
    });
  }, []);

  const checkScroll = () => {
    if (!scrollRef.current) return;
    const { scrollLeft, scrollWidth, clientWidth } = scrollRef.current;
    setCanScrollLeft(scrollLeft > 0);
    setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 10);
  };

  const scroll = (dir: 'left' | 'right') => {
    if (!scrollRef.current) return;
    scrollRef.current.scrollBy({ left: dir === 'left' ? -328 : 328, behavior: 'smooth' });
  };

  return (
    <section className="section-bg-white py-12 lg:py-20">
      <div className="max-w-screen-xl mx-auto px-4 lg:px-8">
        {/* Section header */}
        <div className="flex items-end justify-between mb-6 lg:mb-10">
          <div>
            <p className="section-label mb-2">Featured Properties</p>
            <h2 className="section-heading text-[24px] sm:text-[32px] lg:text-[40px]">Discover Our<br />Featured Listings</h2>
            <p className="section-subheading mt-2 lg:mt-3 text-[13px] lg:text-[15px]" style={{ maxWidth: '420px' }}>
              Hand-picked premium properties across Bengaluru — verified, trusted, and ready to move.
            </p>
          </div>
          <div className="hidden md:flex items-center gap-2">
            <button
              onClick={() => scroll('left')}
              disabled={!canScrollLeft}
              className={`w-10 h-10 rounded-full border-2 flex items-center justify-center transition-all ${
                canScrollLeft
                  ? 'border-[#D93025] text-[#D93025] hover:bg-[#D93025] hover:text-white'
                  : 'border-[#E2E2E7] text-[#C7C7CC] cursor-not-allowed'
              }`}
              aria-label="Previous">
              <ChevronLeft size={17} />
            </button>
            <button
              onClick={() => scroll('right')}
              disabled={!canScrollRight}
              className={`w-10 h-10 rounded-full border-2 flex items-center justify-center transition-all ${
                canScrollRight
                  ? 'border-[#D93025] text-[#D93025] hover:bg-[#D93025] hover:text-white'
                  : 'border-[#E2E2E7] text-[#C7C7CC] cursor-not-allowed'
              }`}
              aria-label="Next">
              <ChevronRight size={17} />
            </button>
          </div>
        </div>

        {/* Horizontal scroll — snap on mobile */}
        <div
          ref={scrollRef}
          onScroll={checkScroll}
          className="flex gap-3 lg:gap-4 overflow-x-auto scrollbar-hide pb-2 -mx-4 px-4 lg:mx-0 lg:px-0"
          style={{ scrollSnapType: 'x mandatory', WebkitOverflowScrolling: 'touch' }}>
          {loading
            ? Array.from({ length: 4 }).map((_, i) => (
                <div key={`sk-${i}`} style={{ scrollSnapAlign: 'start', flexShrink: 0 }}>
                  <SkeletonCard />
                </div>
              ))
            : listings.length > 0
            ? listings.map((listing) => (
                <div key={listing.id} style={{ scrollSnapAlign: 'start', flexShrink: 0 }}>
                  <PropertyCard listing={listing} />
                </div>
              ))
            : (
              <div className="flex items-center justify-center w-full py-16 text-[#A1A1A6] text-[14px]">
                No featured listings available at the moment.
              </div>
            )
          }
        </div>

        {/* View all */}
        <div className="flex justify-center mt-8 lg:mt-10">
          <Link
            href="/listings-page"
            className="flex items-center gap-2 px-6 lg:px-7 py-3 border-2 border-[#D93025] text-[#D93025] rounded-[12px] text-[14px] font-600 hover:bg-[#D93025] hover:text-white transition-all duration-200 group touch-manipulation active:scale-95">
            View All Properties
            <ArrowRight size={15} className="group-hover:translate-x-0.5 transition-transform" />
          </Link>
        </div>
      </div>
    </section>
  );
}